// src/admin/components/editor/MenuBar.jsx
import React, { useState, useRef } from 'react';
import { 
  Bold, 
  Italic, 
  Strikethrough, 
  Heading1, 
  Heading2, 
  Heading3,
  List, 
  ListOrdered,
  Quote,
  Code,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { postsService } from '../../services/postsService';

const MenuBar = ({ editor, isDark }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  if (!editor) return null;

  const buttonClass = (isActive) => `
    p-2 rounded-md transition-all
    ${isActive 
      ? 'bg-blue-800 text-white' 
      : isDark 
        ? 'text-gray-300 hover:bg-white/10' 
        : 'text-gray-600 hover:bg-gray-100'
    }
  `;

  // Handle file upload from PC
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB.');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const response = await postsService.uploadImage(file, (progress) => {
        setUploadProgress(progress);
      });
      
      if (response.url) {
        editor.chain().focus().setImage({ 
          src: response.url,
          alt: file.name,
        }).run();
      }
      
    } catch (error) {
      console.error('Upload failed:', error);
      
      // Fallback: Use Base64 if server upload fails
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          editor.chain().focus().setImage({ 
            src: e.target.result,
            alt: file.name,
          }).run();
          alert('Image uploaded locally. For permanent storage, images will be uploaded to the server.');
        };
        reader.readAsDataURL(file);
      } catch (fallbackError) {
        alert('Failed to upload image. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Trigger file picker
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <div className={`flex flex-wrap gap-1 p-2 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        {/* Text Formatting */}
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={buttonClass(editor.isActive('bold'))}
          title="Bold"
        >
          <Bold size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={buttonClass(editor.isActive('italic'))}
          title="Italic"
        >
          <Italic size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={buttonClass(editor.isActive('strike'))}
          title="Strikethrough"
        >
          <Strikethrough size={18} />
        </button>
        
        <div className="w-px h-8 bg-gray-300/30 mx-1" />
        
        {/* Headings */}
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={buttonClass(editor.isActive('heading', { level: 1 }))}
          title="Heading 1"
        >
          <Heading1 size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={buttonClass(editor.isActive('heading', { level: 2 }))}
          title="Heading 2"
        >
          <Heading2 size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={buttonClass(editor.isActive('heading', { level: 3 }))}
          title="Heading 3"
        >
          <Heading3 size={18} />
        </button>
        
        <div className="w-px h-8 bg-gray-300/30 mx-1" />
        
        {/* Lists */}
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={buttonClass(editor.isActive('bulletList'))}
          title="Bullet List"
        >
          <List size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={buttonClass(editor.isActive('orderedList'))}
          title="Numbered List"
        >
          <ListOrdered size={18} />
        </button>
        
        <div className="w-px h-8 bg-gray-300/30 mx-1" />
        
        {/* Blockquotes & Code */}
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={buttonClass(editor.isActive('blockquote'))}
          title="Blockquote"
        >
          <Quote size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={buttonClass(editor.isActive('codeBlock'))}
          title="Code Block"
        >
          <Code size={18} />
        </button>
        
        <div className="w-px h-8 bg-gray-300/30 mx-1" />
        
        {/* Image Upload */}
        <button
          onClick={triggerFileUpload}
          disabled={uploading}
          className={`${buttonClass(false)} relative`}
          title="Upload Image"
        >
          {uploading ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            <ImageIcon size={18} />
          )}
        </button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) handleFileUpload(file);
          }}
          className="hidden"
        />
      </div>

      {/* Upload Progress */}
      {uploading && uploadProgress > 0 && uploadProgress < 100 && (
        <div className="px-4 py-2 bg-blue-800/10 border-b border-blue-800/20">
          <div className="flex items-center gap-3">
            <Loader2 className="animate-spin text-blue-800" size={16} />
            <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-800 to-blue-500 transition-all duration-300 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span className="text-xs text-blue-800 font-medium">{uploadProgress}%</span>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuBar;